import { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { supabase } from '../services/supabase';
import { SupabaseSong } from '../types/supabase';
import SearchModal from '../components/SearchModal';
import { Player } from '../components/Player';
import { spotifyService } from '../services/spotify';
import { spotifyAuth } from '../services/spotify-auth';
import { clsx } from 'clsx';
import { AlbumArtwork } from '../components/AlbumArtwork';
import spotifyIcon from '../assets/icons/spotify.svg';

interface Song extends SupabaseSong {
  votes: number;
  album_artwork: string | null;
  played: boolean;
}

export default function Playlist() {
  const { id } = useParams<{ id: string }>();
  const [songs, setSongs] = useState<Song[]>([]);
  const [localSongVotes, setLocalSongVotes] = useState<{
    [key: string]: number;
  }>({});
  const [error, setError] = useState<string | null>(null);
  const [playlistName, setPlaylistName] = useState<string>('');
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [currentSong, setCurrentSong] = useState<Song | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isCreator, setIsCreator] = useState(false);

  const CREATOR_COOKIE_NAME = 'tracktoss_creator_id';

  const getCreatorId = () => {
    // First try to get from cookie
    const cookies = document.cookie.split(';');
    const creatorCookie = cookies.find(cookie => cookie.trim().startsWith(`${CREATOR_COOKIE_NAME}=`));
    if (creatorCookie) {
      return creatorCookie.split('=')[1];
    }
    
    // Fallback to localStorage
    return localStorage.getItem(CREATOR_COOKIE_NAME);
  };

  const handleLogin = useCallback(() => {
    if (!id) return;
    // Store the current URL before redirecting
    localStorage.setItem('spotify_return_url', window.location.pathname);
    window.location.href = spotifyAuth.getLoginUrl(id);
  }, [id]);

  useEffect(() => {
    const checkAuth = async () => {
      const token = spotifyAuth.getAccessToken();
      if (token) {
        try {
          const isReady = await spotifyService.setAccessToken(token);
          setIsAuthenticated(isReady);
          if (!isReady && id) {
            handleLogin();
          }
        } catch (error) {
          console.error('Failed to set access token:', error);
          if (id) handleLogin();
        }
      } else if (id && spotifyAuth.isPlaylistAuthorized(id)) {
        handleLogin();
      }
    };

    checkAuth();
  }, [id, handleLogin]);

  useEffect(() => {
    const fetchPlaylistData = async () => {
      if (!id) {
        setError('No playlist ID provided');
        return;
      }

      if (!isAuthenticated) {
        return; // Don't fetch data if not authenticated
      }

      try {
        // Fetch playlist data including creator_id
        const { data: playlistData, error: playlistError } = await supabase
          .from('playlists')
          .select('name, creator_id')
          .eq('id', id)
          .single();

        if (playlistError) throw playlistError;
        if (playlistData) {
          setPlaylistName(playlistData.name);
          
          // Check if current user is creator
          const creatorId = getCreatorId();
          setIsCreator(creatorId === playlistData.creator_id);
        }

        // Fetch songs
        const { data: songsData, error: songsError } = await supabase
          .from('songs')
          .select('*')
          .eq('playlist_id', id)
          .order('votes', { ascending: false });

        if (songsError) throw songsError;

        const songsWithVotes =
          songsData?.map((song) => ({ ...song, votes: song.votes || 0 })) || [];
        setSongs(songsWithVotes);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to fetch playlist data');
      }
    };

    fetchPlaylistData();

    // Subscribe to changes in the songs table
    const subscription = supabase
      .channel('songs_changes')
      .on(
        'postgres_changes',
        {
          event: '*', // Listen for all events (INSERT, UPDATE, DELETE)
          schema: 'public',
          table: 'songs',
          filter: `playlist_id=eq.${id}`,
        },
        async (payload) => {
          console.log('Received change:', payload);

          // Fetch the updated songs list
          const { data: songsData, error: songsError } = await supabase
            .from('songs')
            .select('*')
            .eq('playlist_id', id)
            .order('votes', { ascending: false });

          if (songsError) {
            console.error('Error fetching updated songs:', songsError);
            return;
          }

          const songsWithVotes =
            songsData?.map((song) => ({ ...song, votes: song.votes || 0 })) || [];
          setSongs(songsWithVotes);
        },
      )
      .subscribe();

    // Cleanup subscription on unmount
    return () => {
      subscription.unsubscribe();
    };
  }, [id, isAuthenticated]);

  // Fetch vote data for user voted songs
  useEffect(() => {
    const userVotedSongs = JSON.parse(localStorage.getItem('votedSongs') || '{}');
    setLocalSongVotes(userVotedSongs);
  }, [songs]);

  const handleVote = async (song: SupabaseSong, voteChange: number) => {
    // Check if the user has already voted on this song
    const userVotedSongs = JSON.parse(localStorage.getItem('votedSongs') || '{}');
    const previousVote = userVotedSongs[song.id] || 0;
    let updatedVoteChange = voteChange;
    let removeVotedSong = false;

    if (previousVote) {
      // If the user has already voted, need to check the new vote against the existing vote
      if (previousVote === voteChange) {
        // User is trying to remove their vote
        updatedVoteChange = -voteChange; // Reverse the vote change
        removeVotedSong = true; // Mark song for removal from local storage
      } else {
        // User is changing their vote, update the vote change accordingly
        updatedVoteChange *= 2; // Double the vote change to reflect the change in direction
      }
    }

    try {
      const { error } = await supabase
        .from('songs')
        .update({ votes: song.votes + updatedVoteChange })
        .eq('id', song.id);

      if (error) throw error;

      // Mark the song as voted by the user
      if (removeVotedSong) {
        delete userVotedSongs[song.id]; // Remove the song from local storage
      } else {
        userVotedSongs[song.id] = voteChange; // Update the vote in local storage
      }

      localStorage.setItem('votedSongs', JSON.stringify(userVotedSongs));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update vote');
    }
  };

  const handlePlay = async (song: Song) => {
    if (!isAuthenticated) {
      handleLogin();
      return;
    }

    if (!song) return;

    try {
      // First, update the database state
      const { error: updateError } = await supabase
        .from('songs')
        .update({
          is_currently_playing: true,
          play_progress: 0,
          played_at: new Date().toISOString(),
          last_updated: new Date().toISOString(),
          played: true
        })
        .eq('id', song.id);

      if (updateError) throw updateError;

      // Reset other songs' currently playing state
      const { error: resetError } = await supabase
        .from('songs')
        .update({ is_currently_playing: false })
        .eq('playlist_id', id)
        .neq('id', song.id);

      if (resetError) throw resetError;

      if (currentSong?.id === song.id) {
        if (isPlaying) {
          await spotifyService.pause();
          setIsPlaying(false);
          // Update progress in database on pause
          await supabase
            .from('songs')
            .update({
              play_progress: spotifyService.getCurrentProgress() * 1000,
              last_updated: new Date().toISOString()
            })
            .eq('id', song.id);
        } else {
          const success = await spotifyService.play(song, true);
          if (success) {
            setIsPlaying(true);
          }
        }
      } else {
        const success = await spotifyService.play(song, false);
        if (success) {
          setCurrentSong(song);
          setIsPlaying(true);
        }
      }
    } catch (error) {
      console.error('Error playing song:', error);
      setIsPlaying(false);
      // If there's an error, try to reinitialize the player
      try {
        const isReady = await spotifyService.setAccessToken(spotifyAuth.getAccessToken() || '');
        if (!isReady) {
          setError('Failed to initialize player. Please try again.');
          return;
        }
        // Retry playing after reinitialization
        if (currentSong?.id === song.id) {
          const success = await spotifyService.play(song, true);
          if (success) {
            setIsPlaying(true);
          }
        } else {
          const success = await spotifyService.play(song, false);
          if (success) {
            setCurrentSong(song);
            setIsPlaying(true);
          }
        }
      } catch (retryError) {
        console.error('Failed to reinitialize player:', retryError);
        setError('Failed to play song. Please try again.');
      }
    }
  };

  const handlePlayPause = async () => {
    if (!currentSong && songs.length > 0) {
      // If no song is selected, play the first song in the list
      await handlePlay(songs[0]);
      return;
    }

    if (!currentSong) return;

    try {
      if (isPlaying) {
        await spotifyService.pause();
        // Update progress in database on pause
        await supabase
          .from('songs')
          .update({
            play_progress: spotifyService.getCurrentProgress() * 1000,
            last_updated: new Date().toISOString()
          })
          .eq('id', currentSong.id);
      } else {
        await spotifyService.play(currentSong, true);
      }
      setIsPlaying(!isPlaying);
    } catch (error) {
      console.error('Error toggling playback:', error);
    }
  };

  const handleNext = async () => {
    if (!currentSong || !songs.length) return;

    const currentIndex = songs.findIndex((song) => song.id === currentSong.id);
    const nextIndex = (currentIndex + 1) % songs.length;
    const nextSong = songs[nextIndex];

    await handlePlay(nextSong);
  };

  const handlePrevious = async () => {
    if (!currentSong || !songs.length) return;

    const currentIndex = songs.findIndex((song) => song.id === currentSong.id);
    const previousIndex = (currentIndex - 1 + songs.length) % songs.length;
    const previousSong = songs[previousIndex];

    await handlePlay(previousSong);
  };

  const handleVolumeChange = async (volume: number) => {
    await spotifyService.setVolume(volume);
  };

  // Update play_progress in DB only on play, pause, and seek
  const handleSeek = async (newProgress: number) => {
    if (!currentSong) return;
    await spotifyService.seekTo(newProgress);
    // Update progress in database on seek
    await supabase
      .from('songs')
      .update({
        play_progress: newProgress * 1000,
        last_updated: new Date().toISOString()
      })
      .eq('id', currentSong.id);
  };

  // Update subscription to handle player state changes
  useEffect(() => {
    if (!id) return;

    // Subscribe to changes in the songs table
    const subscription = supabase
      .channel('songs_changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'songs',
          filter: `playlist_id=eq.${id}`,
        },
        async (payload) => {
          console.log('Received change:', payload);

          // Fetch the updated songs list
          const { data: songsData, error: songsError } = await supabase
            .from('songs')
            .select('*')
            .eq('playlist_id', id)
            .order('votes', { ascending: false });

          if (songsError) {
            console.error('Error fetching updated songs:', songsError);
            return;
          }

          const songsWithVotes = songsData?.map((song) => ({ ...song, votes: song.votes || 0 })) || [];
          setSongs(songsWithVotes);

          // Update current song and playing state if needed
          const currentlyPlaying = songsWithVotes.find(song => song.is_currently_playing);
          if (currentlyPlaying) {
            setCurrentSong(currentlyPlaying);
            setIsPlaying(true);
          }
        },
      )
      .subscribe();

    // Cleanup subscription on unmount
    return () => {
      subscription.unsubscribe();
    };
  }, [id, isAuthenticated]);

  if (error) {
    return <div className="text-red-500">{error}</div>;
  }

  return (
    <div className="container mx-auto max-h-[80vh] px-4 py-4 sm:py-8">
      <div className="mx-auto max-w-7xl px-2 sm:px-6 lg:px-8">
        {isAuthenticated ? (
          <>
            {/* Sticky Header Section */}
            <div className="sticky top-0 z-10 bg-background-light shadow-lg dark:bg-background-dark">
              <div className="flex items-center justify-between px-4">
                <h1 className="text-[22px] font-semibold leading-tight text-gray-900 dark:text-gray-100">
                  {playlistName || 'Playlist'}
                </h1>
                <button
                  onClick={() => setIsSearchOpen(true)}
                  className="flex min-h-[44px] min-w-[44px] items-center justify-center gap-2 text-[15px] font-medium text-primary transition-colors hover:text-primary/80"
                >
                  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                    />
                  </svg>
                  <span className="hidden sm:inline">Add Songs</span>
                </button>
              </div>
            </div>

            {/* Player */}
            <div className="mt-4 bg-background-light shadow-lg dark:bg-background-dark">
              <Player
                currentSong={currentSong}
                isPlaying={isPlaying}
                onPlayPause={handlePlayPause}
                onNext={handleNext}
                onPrevious={handlePrevious}
                onVolumeChange={handleVolumeChange}
                isCreator={isCreator}
                onSeek={handleSeek}
              />
            </div>

            {/* Content */}
            <div className="pt-4">
              <div className="grid gap-4">
                {songs.map((song) => (
                  <div
                    key={song.id}
                    className={clsx(
                      "w-full max-w-full overflow-hidden rounded-lg bg-white p-4 shadow transition-shadow hover:shadow-md dark:bg-gray-800",
                      {
                        "border-2 border-[#6466F1] shadow-[0_0_10px_#6466F1]": currentSong?.id === song.id
                      }
                    )}
                  >
                    <div className="flex w-full max-w-full items-center gap-4 overflow-hidden">
                      {/* Album Art */}
                      <div className="h-14 w-14 flex-shrink-0 overflow-hidden rounded-lg">
                        <AlbumArtwork
                          src={song.album_artwork}
                          alt={`${song.title} album art`}
                          className="h-full w-full"
                        />
                      </div>

                      {/* Song Info */}
                      <div className="min-w-0 flex-grow">
                        <h2 className="truncate text-[15px] font-medium leading-tight text-gray-900 dark:text-gray-100">
                          {song.title}
                        </h2>
                        <p className="mt-0.5 truncate text-[13px] leading-tight text-gray-600 dark:text-gray-400">
                          {song.artist}
                        </p>
                        {song.spotify_id && (
                          <div className="mt-0.5 h-[15px]">
                            <img
                              src={spotifyIcon}
                              alt="Spotify"
                              className="h-full w-auto text-gray-500 dark:text-gray-400"
                            />
                          </div>
                        )}
                      </div>

                      {/* Voting Section */}
                      <div className="flex flex-col items-end gap-2">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleVote(song, 1)}
                            className={clsx(
                              'flex min-h-[44px] min-w-[44px] items-center justify-center rounded-lg bg-green-500/10 px-3 py-2 text-green-600 transition-colors hover:bg-green-500/50 dark:text-green-400 sm:w-24',
                              {
                                'bg-green-500/30': localSongVotes[song.id] === 1,
                              },
                            )}
                            aria-label="Upvote"
                          >
                            <span className="sm:hidden">
                              <svg
                                className="h-6 w-6"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M5 15l7-7 7 7"
                                />
                              </svg>
                            </span>
                            <span className="hidden text-[15px] sm:inline">Upvote</span>
                          </button>
                          <button
                            onClick={() => handleVote(song, -1)}
                            className={clsx(
                              'flex min-h-[44px] min-w-[44px] items-center justify-center rounded-lg bg-red-500/10 px-3 py-2 text-red-600 transition-colors hover:bg-red-500/50 dark:text-red-400 sm:w-24',
                              {
                                'bg-red-500/30': localSongVotes[song.id] === -1,
                              },
                            )}
                            aria-label="Downvote"
                          >
                            <span className="sm:hidden">
                              <svg
                                className="h-6 w-6"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M19 9l-7 7-7-7"
                                />
                              </svg>
                            </span>
                            <span className="hidden text-[15px] sm:inline">Downvote</span>
                          </button>
                        </div>
                        <span className="text-[13px] font-medium text-gray-600 dark:text-gray-400">
                          {song.votes || 0} votes
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </>
        ) : (
          <div className="flex min-h-[50vh] flex-col items-center justify-center">
            <h2 className="mb-4 text-2xl font-bold">Connect to Spotify</h2>
            <p className="mb-8 text-gray-600 dark:text-gray-400">
              Connect your Spotify account to play music in this playlist.
            </p>
            <button
              onClick={handleLogin}
              className="flex items-center space-x-2 rounded-full bg-green-500 px-6 py-3 text-white transition-colors hover:bg-green-600"
            >
              <svg className="h-6 w-6" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z" />
              </svg>
              <span>Connect with Spotify</span>
            </button>
          </div>
        )}
      </div>

      <SearchModal
        isOpen={isSearchOpen}
        onClose={() => setIsSearchOpen(false)}
        songs={songs}
        playlistId={id || ''}
        onVote={handleVote}
      />
    </div>
  );
}
